import { clearBlackBox, onLine, previousRun, snapshot } from "../shared/debug";
import "./panels.css";

export interface DebugPanel {
  liveAvailable: boolean;
  hasPrevious(): boolean;
  showLive(): void;
  showPrevious(): void;
  clearPrevious(): void;
}

export function mountDebugPanel(root: HTMLElement): DebugPanel {
  const liveAvailable = new URLSearchParams(location.search).has("debug");
  let previous = previousRun.slice();
  let mode: "live" | "previous" = "live";
  let pending = false;
  let concealed = false;
  root.classList.add("debug-content");
  const description = document.createElement("p");
  description.className = "help";
  const controls = document.createElement("div");
  controls.className = "debug-actions";
  const copy = document.createElement("button");
  copy.type = "button";
  copy.className = "button button--ghost";
  copy.textContent = "Copy log";
  const hide = document.createElement("button");
  hide.type = "button";
  hide.className = "button button--ghost";
  hide.textContent = "Hide log";
  hide.setAttribute("aria-expanded", "true");
  hide.setAttribute("aria-controls", "diagnostic-lines");
  const clear = document.createElement("button");
  clear.type = "button";
  clear.className = "button button--ghost";
  clear.textContent = "Clear previous log";
  clear.hidden = true;
  const status = document.createElement("p");
  status.className = "help";
  status.setAttribute("role", "status");
  const output = document.createElement("pre");
  output.className = "debug-lines";
  output.id = "diagnostic-lines";
  output.tabIndex = 0;
  output.setAttribute("aria-label", "Diagnostic log");
  controls.append(copy, hide, clear);
  root.append(description, controls, status, output);

  const lines = (): string[] => (mode === "previous" ? previous : liveAvailable ? snapshot() : []);
  const render = (): void => {
    pending = false;
    if (concealed) return;
    const stayAtEnd = output.scrollHeight - output.clientHeight - output.scrollTop < 44;
    output.textContent = lines().join("\n") || "No diagnostic lines available.";
    if (stayAtEnd) output.scrollTop = output.scrollHeight;
  };
  const clearPrevious = (): void => {
    previous = [];
    clearBlackBox();
    clear.hidden = true;
    if (mode === "previous") {
      render();
      status.textContent = "Previous log cleared.";
    }
  };
  const show = (next: "live" | "previous"): void => {
    mode = next;
    concealed = false;
    output.hidden = false;
    hide.textContent = "Hide log";
    hide.setAttribute("aria-expanded", "true");
    clear.hidden = mode !== "previous" || !previous.length;
    description.textContent =
      mode === "previous"
        ? "The previous tab ended without a clean exit. These saved lines may explain what happened."
        : "Live diagnostics from this run. The latest 300 lines stay in the log while it is hidden.";
    status.textContent = "";
    render();
  };
  const selectLog = (): void => {
    concealed = false;
    output.hidden = false;
    hide.textContent = "Hide log";
    hide.setAttribute("aria-expanded", "true");
    render();
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(output);
    selection?.removeAllRanges();
    selection?.addRange(range);
    status.textContent = "Log selected. Use your browser’s Copy command.";
  };
  copy.addEventListener("click", () => {
    const text = lines().join("\n");
    const copyingPrevious = mode === "previous";
    if (!text) {
      status.textContent = "There are no log lines to copy.";
      return;
    }
    if (!navigator.clipboard?.writeText) {
      selectLog();
      return;
    }
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        status.textContent = "Log copied.";
        if (copyingPrevious) clearBlackBox();
      })
      .catch(selectLog);
  });
  hide.addEventListener("click", () => {
    concealed = !concealed;
    output.hidden = concealed;
    hide.textContent = concealed ? "Show log" : "Hide log";
    hide.setAttribute("aria-expanded", String(!concealed));
    if (!concealed) render();
  });
  clear.addEventListener("click", clearPrevious);
  if (liveAvailable) {
    onLine(() => {
      if (mode !== "live" || concealed || root.closest("[hidden]") || pending) return;
      // Trace can arrive once per frame; batch text work while the panel is visible.
      pending = true;
      window.setTimeout(() => {
        pending = false;
        if (!root.closest("[hidden]")) render();
      }, 200);
    });
  }
  show("live");
  return {
    liveAvailable,
    hasPrevious: () => previous.length > 0,
    showLive: () => show("live"),
    showPrevious: () => show("previous"),
    clearPrevious,
  };
}
